// @ts-check

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {"brush" | "line"} RedactionTool
 * @typedef {{ tool: RedactionTool, width: number, points: Point[] }} RedactionStroke
 */

export const REDACTION_BRUSH_WIDTHS = [5, 10, 17, 30, 50];
export const DEFAULT_REDACTION_BRUSH_WIDTH = 17;
export const DEFAULT_REDACTION_TOOL = "brush";

const REDACTION_COLOR = "#000000";

/**
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {RedactionStroke[]} strokes
 * @returns {HTMLCanvasElement}
 */
export function createRedactedCanvas(sourceCanvas, strokes) {
  const canvas = document.createElement("canvas");
  canvas.width = sourceCanvas.width;
  canvas.height = sourceCanvas.height;

  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    throw new Error("Could not create redaction canvas");
  }

  ctx.drawImage(sourceCanvas, 0, 0);
  drawRedactionStrokes(ctx, strokes);
  return canvas;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {RedactionStroke[]} strokes
 * @returns {void}
 */
export function drawRedactionStrokes(ctx, strokes) {
  ctx.save();

  for (let i = 0, total = strokes.length; i < total; i++) {
    drawRedactionStroke(ctx, strokes[i]);
  }

  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {RedactionStroke | null} stroke
 * @returns {void}
 */
export function drawRedactionDraft(ctx, stroke) {
  if (stroke === null) {
    return;
  }

  ctx.save();
  drawRedactionStroke(ctx, stroke);
  ctx.restore();
}

/**
 * @param {RedactionStroke[]} strokes
 * @param {RedactionStroke} stroke
 * @returns {RedactionStroke[]}
 */
export function appendRedactionStroke(strokes, stroke) {
  const next = strokes.slice();
  next.push(cloneStroke(stroke));
  return next;
}

/**
 * @param {RedactionStroke[]} strokes
 * @returns {RedactionStroke[]}
 */
export function removeLastRedactionStroke(strokes) {
  let result = strokes;
  if (strokes.length > 0) {
    result = strokes.slice(0, strokes.length - 1);
  }

  return result;
}

/**
 * @param {RedactionTool} tool
 * @param {number} width
 * @param {Point} point
 * @returns {RedactionStroke}
 */
export function createRedactionStroke(tool, width, point) {
  return {
    tool,
    width,
    points: [{ x: point.x, y: point.y }],
  };
}

/**
 * @param {RedactionStroke} stroke
 * @param {Point} point
 * @returns {RedactionStroke}
 */
export function appendRedactionPoint(stroke, point) {
  const next = cloneStroke(stroke);
  const nextPoint = getToolPoint(next, point);
  next.points.push(nextPoint);
  return next;
}

/**
 * @param {RedactionStroke} stroke
 * @returns {RedactionStroke}
 */
export function normalizeRedactionStroke(stroke) {
  let result = cloneStroke(stroke);
  if (result.tool !== "line") {
    return result;
  }

  if (result.points.length < 2) {
    result.points.push({ ...result.points[0] });
  }

  const start = result.points[0];
  const last = result.points[result.points.length - 1];
  result = {
    tool: result.tool,
    width: result.width,
    points: [start, { x: last.x, y: start.y }],
  };

  return result;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {RedactionStroke} stroke
 * @returns {void}
 */
function drawRedactionStroke(ctx, stroke) {
  const normalized = normalizeRedactionStroke(stroke);
  if (normalized.points.length === 0) {
    return;
  }

  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = REDACTION_COLOR;
  ctx.fillStyle = REDACTION_COLOR;
  ctx.lineWidth = normalized.width;

  if (normalized.points.length === 1) {
    drawRedactionDot(ctx, normalized.points[0], normalized.width);
    return;
  }

  ctx.beginPath();
  ctx.moveTo(normalized.points[0].x, normalized.points[0].y);

  for (let i = 1, total = normalized.points.length; i < total; i++) {
    const point = normalized.points[i];
    ctx.lineTo(point.x, point.y);
  }

  ctx.stroke();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Point} point
 * @param {number} width
 * @returns {void}
 */
function drawRedactionDot(ctx, point, width) {
  ctx.beginPath();
  ctx.arc(point.x, point.y, width / 2, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * @param {RedactionStroke} stroke
 * @param {Point} point
 * @returns {Point}
 */
function getToolPoint(stroke, point) {
  let result = { x: point.x, y: point.y };
  if (stroke.tool === "line" && stroke.points.length > 0) {
    result = { x: point.x, y: stroke.points[0].y };
  }

  return result;
}

/**
 * @param {RedactionStroke} stroke
 * @returns {RedactionStroke}
 */
function cloneStroke(stroke) {
  const points = [];
  for (let i = 0, total = stroke.points.length; i < total; i++) {
    points.push({ ...stroke.points[i] });
  }

  return {
    tool: stroke.tool,
    width: stroke.width,
    points,
  };
}
